import React from 'react';
import { createPortal } from 'react-dom';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import { parseHourlyRateOverride } from '../utils/offerLaborPricing';

const PANEL_WIDTH = 220;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 8;

const decimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CELL_FOCUS_CLASS =
  'outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20';

export function OfferHourlyRatePanel({
  open,
  onOpenChange,
  triggerRef,
  value,
  defaultRate,
  onChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  value: number | null;
  defaultRate: number;
  onChange: (next: number | null) => void;
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [pos, setPos] = React.useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const override = parseHourlyRateOverride(value);
  const [text, setText] = React.useState(() => (override != null ? decimal.format(override) : ''));

  React.useEffect(() => {
    setText(override != null ? decimal.format(override) : '');
  }, [override]);

  const updatePos = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = rect.right - PANEL_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + PANEL_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - PANEL_WIDTH);
    }
    setPos({
      bottom: window.innerHeight - rect.top + PANEL_GAP,
      left,
    });
  }, [triggerRef]);

  React.useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
    const onScroll = () => onOpenChange(false);
    const onResize = () => updatePos();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, onOpenChange, updatePos]);

  React.useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onDoc = (e: MouseEvent) => {
      const node = e.target as Node;
      if (triggerRef.current?.contains(node) || panelRef.current?.contains(node)) return;
      onOpenChange(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, onOpenChange, triggerRef]);

  const commitText = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setText('');
      if (override != null) onChange(null);
      return;
    }
    const next = parseHourlyRateOverride(parseGermanNumber(trimmed));
    setText(next != null ? decimal.format(next) : '');
    if (next !== override) onChange(next);
  };

  const placeholder = decimal.format(defaultRate);
  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Abweichender Stundensatz"
      style={{
        position: 'fixed',
        top: pos.top,
        bottom: pos.bottom,
        left: pos.left,
        width: PANEL_WIDTH,
        zIndex: UI_Z_INDEX.portalDropdown,
      }}
      className="rounded-md border border-app-border bg-app-surface p-2.5 text-app-text shadow-lg"
    >
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-app-text-muted">
          Abweichender Stundensatz
        </span>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={text}
            placeholder={placeholder}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              commitText(text);
              onOpenChange(false);
            }}
            onBlur={() => commitText(text)}
            className={`h-9 w-full rounded-md border border-app-border bg-app-surface py-0 pl-3 pr-12 text-right text-sm tabular-nums text-app-text ${CELL_FOCUS_CLASS}`}
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-app-text-subtle">
            €/h
          </span>
        </div>
      </label>
      {override != null ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setText('');
            onChange(null);
            onOpenChange(false);
          }}
          className="mt-2 inline-flex h-9 w-full items-center justify-center rounded-md px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover"
        >
          Standard verwenden
        </button>
      ) : (
        <p className="mt-1.5 text-[11px] leading-snug text-app-text-subtle">
          Leer übernimmt {placeholder} €/h vom Angebot.
        </p>
      )}
    </div>,
    document.body,
  );
}
